/**
 * Markdown-lite dos comunicados (app_banners.body, body_format = 'markdown').
 *
 * Dialeto propositalmente minúsculo — é o que o app precisa renderizar nativo, sem WebView:
 *   **negrito**      -> negrito
 *   _itálico_        -> itálico
 *   \n               -> quebra de linha
 *   emoji            -> passa direto (UTF-8)
 * Não há HTML, link nem imagem: o texto nunca é interpretado como markup, então não há
 * superfície de injeção. Sem aninhamento (um trecho é negrito OU itálico, não os dois).
 *
 * Esta é a implementação de REFERÊNCIA: a pré-visualização do admin usa exatamente este parser,
 * e o app deve reimplementar `parseBannerMarkdown` (a mesma regex) para renderizar igual.
 */

import React from 'react';

// Ordem importa: negrito antes de itálico, senão '**' casaria como '_'..'_' em textos mistos.
const TOKEN_RE = /(\*\*[^*\n]+\*\*|_[^_\n]+_)/g;

/**
 * Quebra uma linha em pedaços tipados.
 * @returns {Array<{type: 'text'|'bold'|'italic', value: string}>}
 */
export function parseBannerMarkdown(line) {
    const out = [];
    let last = 0;
    let m;
    TOKEN_RE.lastIndex = 0;
    while ((m = TOKEN_RE.exec(line)) !== null) {
        if (m.index > last) out.push({ type: 'text', value: line.slice(last, m.index) });
        const tok = m[0];
        if (tok.startsWith('**')) out.push({ type: 'bold', value: tok.slice(2, -2) });
        else out.push({ type: 'italic', value: tok.slice(1, -1) });
        last = m.index + tok.length;
    }
    if (last < line.length) out.push({ type: 'text', value: line.slice(last) });
    return out;
}

/** Texto puro (sem marcadores), para prévia em tabela e tooltip. */
export function stripBannerMarkdown(text) {
    return (text || '')
        .replace(/\*\*([^*\n]+)\*\*/g, '$1')
        .replace(/_([^_\n]+)_/g, '$1')
        .replace(/\s*\n+\s*/g, ' ')
        .trim();
}

export function truncate(text, max = 90) {
    const t = stripBannerMarkdown(text);
    return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}

/** Renderiza o Markdown-lite como o app vai mostrar. */
export function BannerBody({ text, className }) {
    const lines = String(text || '').split('\n');
    return (
        <span className={className}>
            {lines.map((line, li) => (
                <span key={li}>
                    {parseBannerMarkdown(line).map((p, i) => {
                        if (p.type === 'bold') return <strong key={i}>{p.value}</strong>;
                        if (p.type === 'italic') return <em key={i}>{p.value}</em>;
                        return <span key={i}>{p.value}</span>;
                    })}
                    {li < lines.length - 1 && <br />}
                </span>
            ))}
        </span>
    );
}
